import React from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../seo/useSeo';
import SEO_PAGES from '../seo/pages.json';
import PhotoCarousel from '../components/PhotoCarousel';

// The booth itself
const BOOTH_PHOTOS = [
  { src: '/photobooth_services_photo.JPG', alt: 'The Retro Booth set up at a wedding reception' },
  { src: '/colourful-side-shot-1.jpeg',    alt: 'The Retro Booth lit with colourful lighting' },
  { src: '/colourful-side-shot-2.jpeg',    alt: 'Side view of the handmade wooden booth' },
];

// Photos taken in it
const GALLERY = [
  { src: '/20260528_205842_255_3600.jpg', alt: 'Guests posing with props in the booth' },
  { src: '/20260528_213126_496_3600.jpg', alt: 'Group of friends with feather boas and props' },
  { src: '/20260528_221212_606_3600.jpg', alt: 'Guests pulling faces for the camera' },
  { src: '/20260528_211241_869_3600.jpg', alt: 'Wedding guests posing together' },
  { src: '/20260528_220411_301_3600.jpg', alt: 'Large group squeezing into the frame' },
  { src: '/20260528_210103_858_3600.jpg', alt: 'Family portrait taken in the booth' },
];

const STEPS = [
  {
    title: 'Tell us about your event',
    copy: 'Share your date, venue and timings and we’ll confirm availability and send a personalized quote. A 50% deposit secures your date.',
  },
  {
    title: 'We deliver and set up',
    copy: 'We arrive ahead of your start time and have the booth ready in about 30 minutes, so it’s waiting when your guests walk in.',
  },
  {
    title: 'Your guests take over',
    copy: 'Guests tap the touchscreen, strike a pose and get their photos straight to their phone by text or email. When the night wraps up, we pack everything away.',
  },
];

const FEATURES = [
  {
    title: 'Handmade Wooden Booth',
    copy: 'A handbuilt wooden booth mounted on a vintage-style wooden tripod — a retro centrepiece guests actually want to gather around.',
  },
  {
    title: 'Professional DSLR Quality',
    copy: 'A real DSLR camera with studio lighting rather than a tablet camera, for sharp, flattering, print-worthy photos.',
  },
  {
    title: 'Touchscreen & Instant Sharing',
    copy: 'Guests start each session on the touchscreen and receive their photos instantly by SMS and email, plus a digital link to the full event gallery.',
  },
  {
    title: 'Unlimited Photos, Optional Prints',
    copy: 'Every rental includes unlimited digital photos. Add unlimited prints and guests take home classic 2x6 photo strips, printed on the spot.',
  },
  {
    title: 'Black or White Backdrop',
    copy: 'A clean black or white backdrop is included as standard — both photograph beautifully and suit any colour scheme.',
  },
  {
    title: 'Props & a Friendly Attendant',
    copy: 'A box of props to get everyone laughing, and an attendant on hand to keep the line moving and help guests get the shot.',
  },
];

const OCCASIONS = [
  {
    title: 'Weddings',
    copy: 'A photo booth gives guests something to do between dinner and the dance floor — and leaves you with candid photos of everyone who came.',
  },
  {
    title: 'Corporate Events',
    copy: 'The handmade booth and clean black or white backdrop suit a polished brand, from holiday parties to team celebrations and launches.',
  },
  {
    title: 'Graduations & Birthdays',
    copy: 'Friends and family crowd in for group shots, and everyone leaves with photos of the day on their phone.',
  },
];

const RetroBooth = () => {
  useSeo({ ...SEO_PAGES['/retro-booth'], canonicalPath: '/retro-booth' });

  return (
    <div className="retro-booth-page">
      <header className="retro-booth-header">
        <p className="offering-feature-eyebrow">Our Signature</p>
        <h1>
          The Retro Booth
          <span className="retro-booth-h1-sub">Photo Booth Rental in Kansas City</span>
        </h1>
        <p className="retro-booth-intro">
          Handmade wooden booth with touchscreen, professional DSLR camera for stunning photo quality, a black or white backdrop &amp; instant digital sharing.
        </p>
      </header>

      <section className="retro-booth-shots" aria-label="The Retro Booth">
        {BOOTH_PHOTOS.map((photo) => (
          <img key={photo.src} src={photo.src} alt={photo.alt} loading="lazy" decoding="async" />
        ))}
      </section>

      <section className="retro-booth-section">
        <h2>How Our Photo Booth Rental Works</h2>
        <ol className="retro-booth-steps">
          {STEPS.map((step, i) => (
            <li className="retro-booth-step" key={step.title}>
              <span className="retro-booth-step-num" aria-hidden="true">{i + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="retro-booth-features">
        <h2>What&rsquo;s Included With Every Rental</h2>
        <div className="retro-booth-feature-grid">
          {FEATURES.map((feature) => (
            <div className="retro-booth-feature" key={feature.title}>
              <h3>{feature.title}</h3>
              <p>{feature.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="retro-booth-section retro-booth-story">
        <h2>A Local Kansas City Family Business</h2>
        <p>
          Disco Duck Events is a small, locally owned business run by a couple raising a young family
          right here in Kansas City. When you book with us, you&rsquo;re working directly with the owners &mdash;
          the same people who answer your messages are the ones who show up to set up your booth.
        </p>
        <p>
          The Retro Booth isn&rsquo;t a flat-pack kiosk. It&rsquo;s a handbuilt wooden booth paired with a
          professional DSLR camera and studio lighting, so it looks as good at your event as the photos it takes.
          It&rsquo;s the reason guests stop, look twice and want to step up for a picture.
        </p>
        <p>
          It&rsquo;s built for busy nights, too. We&rsquo;ve run it at events with 150 guests, with an attendant
          keeping the line moving and photos landing on phones within moments.
        </p>
      </section>

      <section className="retro-booth-section">
        <h2>A Photo Booth for Every Kind of Celebration</h2>
        <div className="retro-booth-occasions">
          {OCCASIONS.map((occasion) => (
            <div className="retro-booth-feature" key={occasion.title}>
              <h3>{occasion.title}</h3>
              <p>{occasion.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <PhotoCarousel title="Photos From Real Events" photos={GALLERY} />

      <section className="retro-booth-section retro-booth-area">
        <h2>Serving the Kansas City Metro</h2>
        <p>
          We bring the Retro Booth to venues on both the Missouri and Kansas sides of the metro — including
          Kansas City, Overland Park, Olathe, Leawood, Lee&rsquo;s Summit and Independence. Planning an outdoor
          event? We just need a covered area or an indoor backup space. Have more questions? Our{' '}
          <Link to="/faq">photo booth FAQ</Link> covers setup, deposits and cancellations, or browse our{' '}
          <Link to="/gallery">client galleries</Link>.
        </p>
      </section>

      <section className="retro-booth-cta">
        <h2>Ready to Book Your Kansas City Photo Booth?</h2>
        <p>Tell us about your event and we&rsquo;ll send over a personalized quote.</p>
        <Link to="/contact" className="offering-feature-btn" data-track="retro_booth_bottom">
          Check Availability <span aria-hidden="true">&rarr;</span>
        </Link>
      </section>
    </div>
  );
};

export default RetroBooth;
